import classNames from 'classnames';
import type { Options as FocusTrapOptions } from 'focus-trap';
import FocusTrap from 'focus-trap-react';
import type { FC, HTMLAttributes, MouseEvent, ReactNode } from 'react';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { PortalToRoot } from '../PortalToRoot/PortalToRoot';
import { useStableCallback } from '../hooks/useStableCallback';
import { ESCAPE } from '../utils/keyboard/keyboardKeys';

export type OverlayProps = {
  alignment?: 'left' | 'center' | 'right';
  children: ReactNode;
  className?: string;
  onClose?: () => void;
  capToWindowHeight?: boolean;
  noBackdrop?: boolean;
};

const focusTrapOptions: FocusTrapOptions = {
  allowOutsideClick: true,
};

/**
 * The `Overlay` component creates an overlay on the screen.
 * It is designed to serve as a wrapper for other UI components (such as a Modal dialog), positioning them
 * at the top of the z-index visual stack. This makes the wrapped components visually prominent against the backdrop.
 * The component uses a FocusTrap to ensure that keyboard navigation (TAB and other keys) remains within the context of the Overlay.
 *
 * Parameters:
 * - `capToWindowHeight`: When set to true, the overlay's height is constrained to the height of the window.
 * */
export const Overlay: FC<OverlayProps> = ({
  alignment = 'center',
  className,
  children,
  onClose,
  capToWindowHeight = true,
  noBackdrop,
}) => {
  const contentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentContainerRef.current) {
      contentContainerRef.current.focus();
    }
  }, []);

  const handleKeydown = useStableCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (onClose && e.key === ESCAPE) {
        e.stopPropagation();
        onClose();
      }
    },
  );

  /**
   * IMPORTANT: `useClickAway` hook uses similar logic but slightly differently - it detects mouse events
   * outside given element (so it is not suitable for use here). When changing mouse event logic for outside clicks,
   * keep these two consistent as much as possible.
   */
  const handleMouseDown = (e: MouseEvent) => {
    if (onClose && e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * IMPORTANT: The `FocusTrap` component is used to trap focus within the overlay. It requires at least one focusable
   * child to be present inside the overlay. Otherwise, the focus trap throws the following error:
   * Error: "You can’t have a focus-trap without at least one focusable element"
   */
  return (
    <PortalToRoot>
      <FocusTrap focusTrapOptions={focusTrapOptions}>
        <div
          // biome-ignore lint/a11y/useSemanticElements: The div is used instead of a semantic element to allow custom styling and behavior while still providing a role of 'dialog' for accessibility.
          role='dialog'
          tabIndex={-1}
          onKeyDown={handleKeydown}
          className={classNames(
            'z-modal fixed inset-0',
            !capToWindowHeight && 'overflow-auto',
            className,
          )}
        >
          <Backdrop invisible={noBackdrop} />
          <div
            tabIndex={-1}
            ref={contentContainerRef}
            className={classNames(
              'flex min-h-full flex-col justify-center',
              capToWindowHeight && 'max-h-full',
              {
                left: 'items-start',
                center: 'items-center',
                right: 'items-end',
              }[alignment],
            )}
            onMouseDown={handleMouseDown}
          >
            {children}
          </div>
        </div>
      </FocusTrap>
    </PortalToRoot>
  );
};

interface BackdropProps extends HTMLAttributes<HTMLDivElement> {
  invisible?: boolean;
}

/**
 * The `Backdrop` component is used to create a backdrop for the overlay.
 *
 * Parameters (subset):
 * - `invisible`: If true, the backdrop is transparent.
 * */
const Backdrop: FC<BackdropProps> = ({
  className,
  invisible,
  ...restProps
}) => {
  return (
    <div
      tabIndex={-1}
      className={classNames(
        // z-index: -1 to position the backdrop always below the content if the Overlay
        'z-behind fixed inset-0',
        invisible ? 'bg-transparent' : 'bg-state-overlay',
        className,
      )}
      {...restProps}
    />
  );
};
